/**
 * Motion vocabulary for the public marketing surfaces ("Golden Hour" direction).
 *
 * Four tiers, and every element on a page should be able to name which one it
 * belongs to. If a movement doesn't fit a tier, it doesn't ship.
 *
 *  1. ARRIVE   — entrances. A short rise (16–24px) over `--dur-slow`, eased with
 *                `--ease-out`, staggered ~60ms between siblings, fired once when
 *                the element scrolls into view.
 *  2. RESPOND  — micro-feedback. `--dur-fast` hover lifts and presses. The
 *                spring scale-pop (`respondPop`) is RESERVED for save/vote
 *                actions — the moments where the product confirms a choice.
 *                Do not spend it on ordinary buttons.
 *  3. BREATHE  — ambient, always-on movement. Ken Burns on a hero photo, a slow
 *                glow drift on a dusk panel. **At most ONE per viewport** — two
 *                things breathing at once reads as a broken page, not a calm one.
 *  4. THE MOMENT — one orchestrated 2–3s sequence per page, played once, that
 *                shows the product doing its distinctive thing. `MOMENT` below
 *                holds the shared timeline so the beats line up.
 *
 * Reduced motion: the app wraps the router in `<MotionConfig reducedMotion="user">`,
 * which neutralises transform/layout animation globally. That covers ARRIVE and
 * RESPOND. BREATHE and THE MOMENT run on explicit timelines, so components that
 * use them must ALSO read `useReducedMotion()` and render the finished state
 * directly — never a frozen mid-animation frame.
 *
 * Numbers here are the JS mirror of the CSS duration/easing tokens in
 * `src/index.css` (`--dur-*`, `--ease-out`, `--ease-spring`). Change both together.
 */
import type { Transition } from 'motion/react'
import { isReplacingPrerenderedPaint } from '@/lib/prerenderedDocument'

// ── Easing (mirrors --ease-out / --ease-spring) ──────────────────────────────

export const EASE_OUT = [0.22, 1, 0.36, 1] as const
export const EASE_SPRING = [0.34, 1.4, 0.5, 1] as const

// ── Durations in seconds (mirrors --dur-*) ───────────────────────────────────

export const DUR = {
  instant: 0.08,
  fast: 0.14,
  base: 0.22,
  slow: 0.34,
  slower: 0.56,
} as const

// ── 1. ARRIVE ────────────────────────────────────────────────────────────────

/** Default rise distance for an entrance, in px. Spec range is 16–24. */
export const ARRIVE_RISE = 20
/** Delay between staggered siblings, in seconds. */
export const ARRIVE_STAGGER = 0.06
/** Cap on accumulated stagger so long lists don't leave the last item stranded. */
const ARRIVE_STAGGER_CAP = 0.36

export interface ArriveOptions {
  /** Rise distance in px (16–24 per spec). */
  rise?: number
  /** Extra delay in seconds, added on top of the index stagger. */
  delay?: number
  /** Fraction of the element that must be visible before it fires. */
  amount?: number
}

/**
 * Entrance preset for an element that scrolls into view. Spread onto a
 * `motion.*` element: `<motion.div {...arrive()} />`.
 *
 * `index` applies the 60ms sibling stagger (capped), so a mapped list is just
 * `items.map((item, i) => <motion.li {...arrive(i)} />)`.
 */
export function arrive(index = 0, options: ArriveOptions = {}) {
  const { rise = ARRIVE_RISE, delay = 0, amount = 0.2 } = options
  return {
    initial: { opacity: 0, y: rise },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount },
    transition: {
      duration: DUR.slow,
      ease: EASE_OUT,
      delay: Math.min(index * ARRIVE_STAGGER, ARRIVE_STAGGER_CAP) + delay,
    } satisfies Transition,
  }
}

/**
 * ARRIVE for content that is already on screen at load (above the fold), where
 * a viewport trigger would be a no-op race. Same curve, fires on mount.
 */
export function arriveOnMount(delay = 0, options: Omit<ArriveOptions, 'delay' | 'amount'> = {}) {
  const { rise = ARRIVE_RISE } = options
  // On a prerendered page this content is ALREADY on screen and settled (the
  // capture freezes mid-animation styles, so the snapshot IS the end state).
  // React's first commit replaces that DOM; replaying the entrance would take
  // the headline the visitor is reading back to opacity 0 and fade it in
  // again — a visible regression dressed as polish, and the reason the landing
  // measured as ~95% render delay. `initial: false` starts motion at the
  // animate values, which is exactly what the painted DOM already shows, so
  // the swap is invisible. Only that first render is affected; App.tsx clears
  // the flag once it commits, so client-side navigation still animates.
  const settled = isReplacingPrerenderedPaint()
  return {
    initial: settled ? (false as const) : { opacity: 0, y: rise },
    animate: { opacity: 1, y: 0 },
    transition: { duration: DUR.slow, ease: EASE_OUT, delay } satisfies Transition,
  }
}

// ── 2. RESPOND ───────────────────────────────────────────────────────────────

/** Hover lift + press for an interactive card or tile. */
export const respondLift = {
  whileHover: { y: -3 },
  whileTap: { y: 0 },
  transition: { duration: DUR.fast, ease: EASE_OUT } satisfies Transition,
}

/** Press-only feedback for controls that shouldn't move on hover. */
export const respondPress = {
  whileTap: { scale: 0.985 },
  transition: { duration: DUR.fast, ease: EASE_OUT } satisfies Transition,
}

/**
 * Spring scale-pop. RESERVED for save/vote confirmations — the product's
 * "your choice registered" beat. Using it on ordinary buttons burns the signal.
 */
export const respondPop: Transition = { duration: DUR.base, ease: EASE_SPRING }

// ── 3. BREATHE (max one per viewport) ────────────────────────────────────────

/** Ken Burns cycle length, seconds. Spec range is 28–40. */
export const BREATHE_KEN_BURNS_S = 34
/** Glow drift cycle length, seconds. */
export const BREATHE_GLOW_S = 30

/**
 * Slow push-in + drift for a hero photograph. Caller must gate on
 * `useReducedMotion()` and render the plain image instead when it's set.
 */
export function kenBurns(seconds = BREATHE_KEN_BURNS_S) {
  return {
    initial: { scale: 1, x: '0%', y: '0%' },
    animate: { scale: 1.08, x: '-1.5%', y: '-1%' },
    transition: {
      duration: seconds,
      ease: 'easeInOut',
      repeat: Infinity,
      repeatType: 'reverse',
    } satisfies Transition,
  }
}

/** Slow opacity/position drift for the glow blooms on a dusk panel. */
export function glowDrift(seconds = BREATHE_GLOW_S) {
  return {
    animate: { opacity: [0.45, 0.85, 0.45], scale: [1, 1.12, 1] },
    transition: {
      duration: seconds,
      ease: 'easeInOut',
      repeat: Infinity,
    } satisfies Transition,
  }
}

// ── 4. THE MOMENT ────────────────────────────────────────────────────────────

/**
 * Shared beat sheet for the page's single orchestrated sequence, in seconds
 * from mount. The landing hero plays: copy arrives, the plan card rises, then
 * the crew assembles and the vote tally lands (~2.6s total).
 */
export const MOMENT = {
  eyebrow: 0.08,
  headline: 0.18,
  sub: 0.34,
  action: 0.5,
  card: 0.86,
  crew: 1.3,
} as const
